import { AxiosError } from "axios";
import { useEffect, useState, useMemo, useCallback } from "react";
import { useParams } from "react-router-dom";
import { Stack } from "@/core/components/ui/stack";
import { toast } from "@/core/components/ui/toast";
import { PAYOUT_STATUS } from "@/payouts/constants";
import {
  useClaimCredit,
  useGetInitiateVerification,
  useGetPayoutByEndpoint,
  cleanupPayoutVerification,
} from "@/payouts/query";
import {
  AlreadyPaidOut,
  CreditClaimed,
  ErrorState,
  LoadingPayout,
  SelectPayoutType,
  VerifyCode,
} from "./claim-payout-steps";
import { ClaimPayoutStep } from "./claimPayout";
import { CLAIM_PAYOUT_STEPS } from "./constants";

const PayoutConfirmationContent = () => {
  const { payoutEndpoint } = useParams();
  const [error, setError] = useState<string | null>(null);
  const [redirectUrl, setRedirectUrl] = useState<string>("");
  const [isRedirecting, setIsRedirecting] = useState(false);

  const {
    data: payoutData,
    isLoading: isLoadingPayout,
    isError: isPayoutError,
  } = useGetPayoutByEndpoint(payoutEndpoint);

  const {
    data: initiateData,
    refetch: resendCode,
    isRefetching,
  } = useGetInitiateVerification(payoutEndpoint);

  const { mutate: claimCredit, isPending: isClaimingCredit } =
    useClaimCredit(payoutEndpoint);

  const currentStep = useMemo<ClaimPayoutStep>(() => {
    if (error) return CLAIM_PAYOUT_STEPS.error;
    if (isLoadingPayout || isRedirecting) return CLAIM_PAYOUT_STEPS.loading;
    if (isRefetching) return CLAIM_PAYOUT_STEPS.verify;
    if (!payoutData) return CLAIM_PAYOUT_STEPS.loading;

    const status = payoutData.status;
    if (status === PAYOUT_STATUS.CREDIT_CLAIMED)
      return CLAIM_PAYOUT_STEPS.claimedCredit;
    if (status === PAYOUT_STATUS.PAID_OUT) return CLAIM_PAYOUT_STEPS.paidOut;
    if (redirectUrl) return CLAIM_PAYOUT_STEPS.paymentChoice;
    if (isClaimingCredit) return CLAIM_PAYOUT_STEPS.loading;

    return CLAIM_PAYOUT_STEPS.verify;
  }, [
    isLoadingPayout,
    isRefetching,
    isRedirecting,
    payoutData,
    redirectUrl,
    isClaimingCredit,
    error,
  ]);

  useEffect(() => {
    if (isPayoutError) {
      setError("Failed to load payout details. Please try again later.");
      return;
    }
  }, [isPayoutError]);

  const handleRedirect = useCallback(
    (url: string) => {
      setIsRedirecting(true);
      window.location.href = url;
    },
    [setIsRedirecting],
  );

  const handleVerified = useCallback(
    (url: string, creditEnabled: boolean) => {
      setRedirectUrl(url);

      if (!creditEnabled) {
        toast.success("Code verified! Redirecting to payouts...");
        cleanupPayoutVerification(payoutEndpoint);
        handleRedirect(url);
      }
    },
    [payoutEndpoint, cleanupPayoutVerification, handleRedirect],
  );

  const handlePaymentChoice = (type: "credit" | "cash") => {
    if (!redirectUrl) return;

    if (type === "credit") {
      claimCredit(undefined, {
        onSuccess: () => {
          cleanupPayoutVerification(payoutEndpoint);
          toast.success("Credit claimed successfully", {
            description: `Your credit has been added to your account with ${payoutData?.business_name}.`,
          });
        },
        onError: (error) => {
          const axiosError = error as AxiosError<{ error?: string }>;
          if (
            axiosError?.response?.status === 422 &&
            axiosError?.response?.data?.error?.includes("already claimed")
          ) {
            setError(
              `Your credit is already available in your account with ${payoutData?.business_name}.`,
            );
            return;
          }

          toast.error("Failed to claim credit", {
            description:
              axiosError?.response?.data?.error || "Please try again",
          });
        },
      });
    } else {
      cleanupPayoutVerification(payoutEndpoint);
      handleRedirect(redirectUrl);
    }
  };

  const handleRetry = () => {
    window.location.reload();
  };

  if (currentStep === CLAIM_PAYOUT_STEPS.loading) {
    return <LoadingPayout />;
  }

  if (currentStep === CLAIM_PAYOUT_STEPS.error) {
    return <ErrorState error={error} onRetry={handleRetry} />;
  }

  if (currentStep === CLAIM_PAYOUT_STEPS.paidOut) {
    return <AlreadyPaidOut payout={payoutData!} />;
  }

  if (currentStep === CLAIM_PAYOUT_STEPS.paymentChoice) {
    return (
      <SelectPayoutType
        verificationData={initiateData!}
        onSelectPaymentType={handlePaymentChoice}
      />
    );
  }

  if (currentStep === CLAIM_PAYOUT_STEPS.claimedCredit) {
    return (
      <CreditClaimed
        verificationData={initiateData!}
        isProcessing={isClaimingCredit}
      />
    );
  }

  return (
    <VerifyCode
      payout={payoutData!}
      verificationData={initiateData!}
      onVerified={handleVerified}
      onResendCode={resendCode}
    />
  );
};

export const ClaimPayout = () => (
  <Stack className="flex-1 overflow-hidden bg-card" fullWidth>
    <PayoutConfirmationContent />
  </Stack>
);

export default ClaimPayout;
